import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import { Plus, Archive, Check, Tag, X, Zap, ArrowRight, Circle, RefreshCw } from "lucide-react";

const COLORS = { text: "#A1A1AA", link: "#5A90F5", voice: "#A855F7", email: "#FF8A00" };

export default function Capture() {
  const { current } = useWorkspace();
  const [captures, setCaptures] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active | archived
  const [tagFilter, setTagFilter] = useState(null);
  const inputRef = useRef(null);
  const wsId = current?.workspace_id;

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/captures`, { params: { archived: filter === "archived" } });
      setCaptures(data);
    } catch {} finally { setLoading(false); }
  }, [wsId, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const add = async () => {
    const text = input.trim();
    if (!text || !wsId) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/captures`, { title: text });
      setCaptures(c => [data, ...c]);
      setInput("");
    } catch { toast.error("Failed to add capture"); }
  };

  const archive = async (id) => {
    try {
      await api.put(`/workspaces/${wsId}/captures/${id}`, { archived: true });
      setCaptures(c => c.filter(x => x.capture_id !== id));
    } catch {}
  };

  const process = async (id) => {
    try {
      await api.put(`/workspaces/${wsId}/captures/${id}`, { processed: true });
      setCaptures(c => c.map(x => x.capture_id === id ? { ...x, processed: true } : x));
    } catch {}
  };

  const del = async (id) => {
    try {
      await api.delete(`/workspaces/${wsId}/captures/${id}`);
      setCaptures(c => c.filter(x => x.capture_id !== id));
    } catch {}
  };

  const allTags = [...new Set(captures.flatMap(c => c.tags || []))];
  const visible = tagFilter ? captures.filter(c => c.tags?.includes(tagFilter)) : captures;

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      {/* Header */}
      <div className="px-8 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <div>
          <h1 className="text-[18px] font-bold text-[#EDEDED]">Capture</h1>
          <p className="text-[11px] text-[#3A3A3A] mt-0.5">{captures.filter(c => !c.processed).length} unprocessed</p>
        </div>
        <div className="flex gap-2">
          {["active","archived"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-[11px] capitalize transition-colors border ${filter === f ? "border-[#5A90F5]/40 bg-[#5A90F5]/10 text-[#5A90F5]" : "border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA]"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Quick input */}
      <div className="px-8 py-4 border-b border-[#1A1A1A]">
        <div className="flex gap-3">
          <div className="flex-1 flex items-center gap-3 bg-[#111] border border-[#2A2A2A] rounded-xl px-4 py-3 focus-within:border-[#5A90F5]/50 transition-colors">
            <Zap className="w-4 h-4 text-[#3A3A3A] flex-shrink-0" />
            <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") add(); }}
              placeholder="Capture a thought, idea, or fragment..."
              className="flex-1 bg-transparent text-[13px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none" />
            {input && <button onClick={() => setInput("")} className="text-[#3A3A3A] hover:text-[#52525B]"><X className="w-3.5 h-3.5" /></button>}
          </div>
          <button onClick={add} disabled={!input.trim()}
            className="px-4 py-3 rounded-xl bg-[#5A90F5] hover:bg-[#4A80E5] text-white text-[12px] font-semibold disabled:opacity-30 transition-colors">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tag filter */}
      {allTags.length > 0 && (
        <div className="px-8 py-2 border-b border-[#1A1A1A] flex items-center gap-2 overflow-x-auto">
          <button onClick={() => setTagFilter(null)}
            className={`px-2.5 py-1 rounded-lg text-[10px] flex-shrink-0 border transition-colors ${!tagFilter ? "border-[#5A90F5]/40 bg-[#5A90F5]/10 text-[#5A90F5]" : "border-[#2A2A2A] text-[#3A3A3A]"}`}>
            All
          </button>
          {allTags.map(t => (
            <button key={t} onClick={() => setTagFilter(t === tagFilter ? null : t)}
              className={`px-2.5 py-1 rounded-lg text-[10px] flex-shrink-0 border transition-colors ${tagFilter === t ? "border-[#5A90F5]/40 bg-[#5A90F5]/10 text-[#5A90F5]" : "border-[#2A2A2A] text-[#3A3A3A] hover:text-[#52525B]"}`}>
              {t}
            </button>
          ))}
        </div>
      )}

      {/* Captures list */}
      <div className="flex-1 overflow-y-auto px-8 py-4 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-12"><RefreshCw className="w-4 h-4 text-[#3A3A3A] animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#111] border border-[#2A2A2A] flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#3A3A3A]" />
            </div>
            <p className="text-[13px] text-[#3A3A3A]">Nothing captured yet</p>
            <p className="text-[11px] text-[#2A2A2A]">Type something above and press Enter</p>
          </div>
        ) : (
          visible.map(c => (
            <div key={c.capture_id}
              className={`group flex items-start gap-3 p-4 rounded-xl border transition-all ${c.processed ? "border-[#1A1A1A] bg-transparent opacity-50" : "border-[#2A2A2A] bg-[#111] hover:border-[#3A3A3A]"}`}>
              <button onClick={() => process(c.capture_id)} className={`mt-0.5 flex-shrink-0 ${c.processed ? "text-[#3A3A3A]" : "text-[#3A3A3A] hover:text-[#5A90F5]"}`}>
                {c.processed ? <Check className="w-4 h-4 text-[#00C781]" /> : <Circle className="w-4 h-4" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] leading-snug ${c.processed ? "line-through text-[#3A3A3A]" : "text-[#EDEDED]"}`}>{c.title}</p>
                {c.body && <p className="text-[11px] text-[#52525B] mt-1 line-clamp-2">{c.body}</p>}
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-[9px] text-[#3A3A3A]">{new Date(c.created_at).toLocaleDateString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  {c.tags?.map(t => <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#1A1A1A] text-[#52525B] border border-[#2A2A2A]">{t}</span>)}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0">
                <button onClick={() => archive(c.capture_id)} className="p-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#3A3A3A] hover:text-[#A1A1AA]" title="Archive"><Archive className="w-3.5 h-3.5" /></button>
                <button onClick={() => del(c.capture_id)} className="p-1.5 rounded-lg hover:bg-[#EF4444]/10 text-[#3A3A3A] hover:text-[#EF4444]" title="Delete"><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
